import { gql } from '__generated__';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { AuLicenseData, parseConfig } from '../helpers';
import ParticipantFieldInputProps from '../ParticipantFieldInputProps';
import UI from '../../UI';
import useIntegerInput from '../../useIntegerInput';
import useQuery from '../../../api/useQuery';

export const GetAtletiekunieClubsQuery = gql(`
  query GetAtletiekunieClubs {
    integrations {
      atletiekunie {
        clubs {
          id
          name
        }
      }
    }
  }
`);

const AuLicenseInput = ({
  value, onChange, onBlur, field, touched, errors, validating,
}: ParticipantFieldInputProps) => {
  const { t } = useTranslation('common');
  const integerInput = useIntegerInput();
  const idRef = useRef(Math.round(Math.random() * 1000000));

  const { data } = useQuery(GetAtletiekunieClubsQuery);

  const clubs = data?.integrations?.atletiekunie?.clubs;

  const licenseData: AuLicenseData = value.value ? JSON.parse(value.value) : {
    club_id: null,
    member_id: null,
  };

  const form = useForm({
    values: licenseData,
  });

  const parsedConfig = parseConfig(field.config);
  const requireMemberId = parsedConfig.require_competition_license
    || parsedConfig.require_elearning_bronze
    || parsedConfig.require_elearning_silver
    || parsedConfig.require_elearning_gold
    || false;

  /**
   * Used so that we can trigger change events only when the value has changed.
   */
  const valueRef = useRef(value.value);

  useEffect(() => {
    let newValue: string | null = null;

    if (form.values.club_id || form.values.member_id) {
      // Find selected club to append the name
      const selected = (clubs && clubs.filter((club) => (
        form.values.club_id === club.id
      ))) || [];

      newValue = JSON.stringify({
        club_id: form.values.club_id,
        member_id: form.values.member_id,
        club_name: selected.length > 0 ? selected[0].name : null,
      });
    }

    if (newValue !== valueRef.current) {
      onChange({ value: newValue });
      valueRef.current = newValue;
    }
  }, [form.values.club_id, form.values.member_id, clubs, onChange]);

  const handleBlur = () => {
    if (form.touched.club_id && form.touched.member_id) {
      onBlur?.();
    }
  };

  const clubIds = clubs ? clubs.map((club) => club.id) : [];

  return (
    <UI.InputGroup sc={{ valid: !errors?.value, touched }}>
      <UI.FormGrid>
        <UI.InputGroup>
          <UI.InputLabel htmlFor={`Field_${idRef.current}_ClubId`}>
            {field.title}
            {' '}
            {field.required && <UI.RequiredMark />}
            {validating && (
              <>
                {' '}
                <UI.Icon>
                  <UI.Loader sc={{ brand: 'gray.400' }} />
                </UI.Icon>
              </>
            )}
          </UI.InputLabel>
          {field.description && (
            <UI.InputDescription as="div">
              <UI.HTML html={field.description} />
            </UI.InputDescription>
          )}
          <UI.Select
            {...form.select('club_id', [null, ...clubIds], { onBlur: handleBlur })}
            id={`Field_${idRef.current}_ClubId`}
            sc={{ hasValue: !!form.values.club_id }}
          >
            <option value={0}>{t('atletiekunie.club')}</option>
            {(clubs || []).map((club, index) => (
              <option {...form.option(index + 1)} key={index + 1}>{club.name}</option>
            ))}
          </UI.Select>
          {requireMemberId && (
            <UI.DebouncedInput
              {...form.text('member_id', { inputHandler: integerInput(), onBlur: handleBlur })}
              format={integerInput().format}
              maxLength={7}
              inputMode="numeric"
              match={/[\d]/g}
              placeholder={t('atletiekunie.member_id_placeholder')}
              id={`Field_${idRef.current}_MemberId`}
              sc={{ mt: 2 }}
            />
          )}
        </UI.InputGroup>
      </UI.FormGrid>
      <UI.ErrorMessages attribute={field.title} errors={errors?.value} />
    </UI.InputGroup>
  );
};

export default AuLicenseInput;
